import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import deleteIcon from '@/assets/icons/deleteIcon.svg';
import historyIcon from '@/assets/icons/historyIcon.svg';
import intercomIcon from '@/assets/icons/intercomIcon.svg';
import plusIcon from '@/assets/icons/plusIcon.svg';
import { Button } from '@/components/ui/button';
import { UserInitialIcon } from '@/components/ui/user-initial-icon';
import { ROUTES } from '@/utils/routeUtils';

interface TopNavbarActionsProps {
  currentSessionId: string | null;
  historyRoute?: string;
  isDeleting: boolean;
  onDeleteSession: () => void;
  onNewSession: () => void;
}

export function TopNavbarActions({
  currentSessionId,
  historyRoute,
  isDeleting,
  onDeleteSession,
  onNewSession,
}: TopNavbarActionsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="flex h-[44px] w-full items-center justify-end pt-2 pr-3 pb-2 pl-3">
      <div className="flex items-center gap-2">
        {/* Icons group with rounded background - on the right */}
        <div className="bg-card flex h-7 w-[92px] items-center justify-between rounded-full px-2 py-1 shadow-sm">
          <Button
            aria-label={t('narrative.startNewConversation')}
            className="hover:bg-elevated-subtle flex aspect-square h-6 w-6 items-center justify-center rounded-full bg-transparent p-0 transition-colors"
            disabled={!currentSessionId}
            onClick={onNewSession}
            size="icon"
            variant="ghost"
          >
            <img
              alt={t('assistant.topbar.addAlt')}
              className="icon-themed h-[14px] w-[14px]"
              src={plusIcon}
            />
          </Button>
          <Button
            aria-label={t('assistant.topbar.historyAria')}
            className="hover:bg-elevated-subtle flex aspect-square h-6 w-6 items-center justify-center rounded-full bg-transparent p-0 transition-colors"
            onClick={() => navigate(historyRoute ?? ROUTES.HISTORY)}
            size="icon"
            variant="ghost"
          >
            <img
              alt={t('assistant.topbar.historyAlt')}
              className="icon-themed h-[14px] w-[14px]"
              src={historyIcon}
            />
          </Button>
          <Button
            aria-label={t('assistant.topbar.deleteAria')}
            className="hover:bg-elevated-subtle flex aspect-square h-6 w-6 items-center justify-center rounded-full bg-transparent p-0 transition-colors"
            disabled={!currentSessionId || isDeleting}
            onClick={onDeleteSession}
            size="icon"
            variant="ghost"
          >
            <img
              alt={t('assistant.topbar.deleteAlt')}
              className="icon-themed h-[14px] w-[14px]"
              src={deleteIcon}
            />
          </Button>
        </div>
        <Button
          aria-label={t('assistant.topbar.intercomAria')}
          className="hover:bg-elevated-subtle flex h-7 w-7 items-center justify-center rounded-full bg-transparent p-0 transition-colors"
          onClick={() => showIntercom()}
          size="icon"
          variant="ghost"
        >
          <img
            alt={t('assistant.topbar.intercomAlt')}
            className="h-[20px] w-[20px]"
            src={intercomIcon}
          />
        </Button>
        <UserInitialIcon
          backgroundColor="black"
          className="shadow-[0px_5px_12px_2px_#B3B7C50D,0px_1px_3px_0px_#B3B7C533]"
          size="md"
        />
      </div>
    </div>
  );
}
